import React, { ReactNode, createContext, useContext, useState } from 'react';

export interface TenantNode {
	id: string;
	name: string;
	parentId: string | null;
	metadata?: Record<string, any>;
	children: TenantNode[];
}

interface TenantNodeContextType {
	fullTree: TenantNode[] | null;
	setFullTree: (tree: TenantNode[]) => void;
	getAccessibleNodes: (userNodeIds: string[]) => TenantNode[];
	isLoading: boolean;
	setIsLoading: (loading: boolean) => void;
}

const TenantNodeContext = createContext<TenantNodeContextType | undefined>(undefined);

export const useTenantNodeContext = () => {
	const context = useContext(TenantNodeContext);
	if (!context) {
		throw new Error('useTenantNodeContext must be used within a TenantNodeProvider');
	}
	return context;
};

interface TenantNodeProviderProps {
	children: ReactNode;
}

export const TenantNodeProvider: React.FC<TenantNodeProviderProps> = ({ children }) => {
	const [fullTree, setFullTree] = useState<TenantNode[] | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	const getAccessibleNodes = (userNodeIds: string[]): TenantNode[] => {
		if (!fullTree || userNodeIds.length === 0) {
			return [];
		}

		// First, find all accessible node IDs including their descendants
		const getAllAccessibleNodeIds = (): string[] => {
			const accessibleIds = new Set<string>();

			// Check if user has access to any root nodes
			const hasRootAccess = userNodeIds.length > 0;

			if (hasRootAccess) {
				// User has root access - include all nodes and their descendants
				const traverseNodes = (nodeList: TenantNode[]) => {
					nodeList.forEach((node) => {
						accessibleIds.add(node.id);
						if (node.children.length > 0) {
							traverseNodes(node.children);
						}
					});
				};

				traverseNodes(fullTree);
			}

			return Array.from(accessibleIds);
		};

		const allAccessibleIds = getAllAccessibleNodeIds();

		// Now build the tree structure with accessible nodes
		const buildAccessibleTree = (nodes: TenantNode[]): TenantNode[] => {
			const accessibleNodes: TenantNode[] = [];

			nodes.forEach((node) => {
				// Check if this node is accessible (directly or as descendant)
				const isAccessible = allAccessibleIds.includes(node.id);

				if (isAccessible) {
					accessibleNodes.push({
						...node,
						children: buildAccessibleTree(node.children),
					});
				}
			});

			return accessibleNodes;
		};

		const result = buildAccessibleTree(fullTree);
		return result;
	};

	const value: TenantNodeContextType = {
		fullTree,
		setFullTree,
		getAccessibleNodes,
		isLoading,
		setIsLoading,
	};

	return <TenantNodeContext.Provider value={value}>{children}</TenantNodeContext.Provider>;
};
